import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useTitle } from 'react-use';

import { Switch } from '@/components/ui/switch';
import { useDeskId } from '@/hooks/useDeskId';
import { useIntegrations } from '@/hooks/useIntegrations';
import { getPermissions } from '@/redux/permissions/selectors';
import { EmailIntegration, IntegrationStatus } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import { AgentInfo } from './components/AgentInfo';
import DeleteButton from './components/DeleteButton';
import { DNSConfiguration } from './components/DNSConfiguration';
import { ForwardingConfiguration } from './components/ForwardingConfiguration';

/**
 * EmailSettings - Settings page for email integrations
 *
 * Features:
 * - Sender configuration (email and name)
 * - DNS verification for email domain
 * - Email forwarding configuration
 * - Webhook configuration
 * - Advanced settings
 * - Active/inactive toggle
 * - Delete integration option
 *
 * @returns {JSX.Element} The email settings component
 */
const EmailSettings = () => {
  const { t } = useTranslation();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );
  const deskId = useDeskId();
  const { integrationId } = useParams();
  const { integration, updateIntegration, verifyIntegration } = useIntegrations<EmailIntegration>(
    deskId,
    integrationId
  );

  const [isUpdateNameLoading, setIsUpdateNameLoading] = useState<boolean>(false);
  const [isDNSVerifyLoading, setIsDNSVerifyLoading] = useState<boolean>(false);

  useTitle(t('pages.email', { 0: integration?.config?.from_email ?? '-' }));

  const onToggleEnabled = (enabled: boolean) => {
    updateIntegration({ ...integration, active: enabled });
  };

  const onSenderInfoVerify = (data: { from_email: string; from_name: string }) => {
    setIsUpdateNameLoading(true);
    updateIntegration(
      { ...integration, config: { ...integration.config, from_name: data.from_name } },
      {
        onSettled: () => {
          setIsUpdateNameLoading(false);
        },
      }
    );
  };

  const onDNSVerify = () => {
    setIsDNSVerifyLoading(true);
    verifyIntegration(undefined, {
      onSettled: () => {
        // Add a small delay to give the illusion of loading
        setTimeout(() => {
          setIsDNSVerifyLoading(false);
        }, 1000);
      },
    });
  };

  // Check if all DNS records are verified
  const isDNSConfigured = Object.values(integration?.config?.dns ?? {}).every(
    (dnsRecord) => dnsRecord?.valid
  );

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <div className="flex flex-col">
        <div className="mb-5 flex items-center justify-between">
          <h3 className="text-xl leading-none font-semibold">{t('email.sender_information')}</h3>
          <Switch
            label={t('email.enable_sender_email', { 0: integration?.config?.from_email || '' })}
            checked={integration?.active ?? false}
            onCheckedChange={onToggleEnabled}
            disabled={!canWrite}
            className="data-[state=checked]:bg-accent"
          />
        </div>
        <div className="rounded-md rounded-xl bg-bg-muted p-1">
          <AgentInfo
            emailName={integration?.config?.from_name}
            emailAddress={integration?.config?.from_email}
            canWrite={canWrite}
            isLoading={isUpdateNameLoading}
            onVerify={onSenderInfoVerify}
          />
          <DNSConfiguration
            integration={integration}
            canWrite={canWrite}
            onVerify={onDNSVerify}
            isVerifyLoading={
              isDNSVerifyLoading || integration?.status === IntegrationStatus.VERIFYING
            }
          />
        </div>
      </div>
      {isDNSConfigured && <ForwardingConfiguration integration={integration} canWrite={canWrite} />}
      <DeleteButton integration={integration} canWrite={canWrite} />
    </div>
  );
};

export default EmailSettings;
